import { useUserActionsHooks } from '@api/userActionsApiHandler';
import Checkbox from '@components/Atoms/Checkbox';
import Modal from '@components/Modal';
import {
  TRAUMA_CONTACT_CANCEL_LABEL,
  TRAUMA_CONTACT_CONFIRM_LABEL,
  TRAUMA_CONTACT_CONSENT_LABEL,
  TRAUMA_CONTACT_DONE_LABEL,
  TRAUMA_CONTACT_EMAIL_LABEL,
  TRAUMA_CONTACT_ERROR_MESSAGE,
  TRAUMA_CONTACT_FULL_NAME_LABEL,
  TRAUMA_CONTACT_IDENTITY_BODY,
  TRAUMA_CONTACT_IDENTITY_HEADING,
  TRAUMA_CONTACT_MODAL_INTRO,
  TRAUMA_CONTACT_MODAL_TITLE,
  TRAUMA_CONTACT_SUCCESS_DESCRIPTION_PREFIX,
  TRAUMA_CONTACT_SUCCESS_DESCRIPTION_SUFFIX,
  TRAUMA_CONTACT_SUCCESS_TITLE,
} from '@config/traumaContactContent';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { showToast } from '@shared/atoms';
import React, { useEffect, useState } from 'react';
import { SystemButton } from '../SystemButton/SystemButton';
import styles from './index.module.scss';

type TraumaContactStep = 'confirm' | 'success';

interface TraumaContactDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Action whose library `url` holds the recipient email (resolved server-side). */
  actionId: number;
  /**
   * Called after a successful send so the parent can complete the action.
   * Should not refetch/unmount the card yet - the success step must stay open until Done.
   */
  onSuccess?: () => void | Promise<void>;
  /** Called when the user dismisses the success step (Done). Parent should close + refetch. */
  onDone?: () => void;
}

const readLoggedInUserIdentity = (): { fullName: string; email: string } => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}') as {
      email?: string;
      firstName?: string;
      lastName?: string;
      forename?: string;
      surname?: string;
    };
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}') as {
      forename?: string;
      surname?: string;
    };

    const firstName = userDetails.forename || user.forename || user.firstName || '';
    const lastName = userDetails.surname || user.surname || user.lastName || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

    return {
      fullName: fullName || '-',
      email: user.email?.trim() || '-',
    };
  } catch {
    return { fullName: '-', email: '-' };
  }
};

/**
 * Confirm + success popup for TRAUMA_CONTACT actions.
 * On confirm, BE sends an auto-generated email - the user does not enter a message.
 */
export const TraumaContactDrawer: React.FC<TraumaContactDrawerProps> = ({
  open,
  onClose,
  actionId,
  onSuccess,
  onDone,
}) => {
  const sendMutation = useUserActionsHooks.useSendActionContactEmail();
  const isSubmitting = sendMutation.isPending;

  const [step, setStep] = useState<TraumaContactStep>('confirm');
  const [consented, setConsented] = useState(false);
  const identity = open ? readLoggedInUserIdentity() : { fullName: '-', email: '-' };

  useEffect(() => {
    if (open) {
      setStep('confirm');
      setConsented(false);
    }
  }, [open]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    onClose();
  };

  const handleConfirm = async () => {
    if (!consented || isSubmitting) {
      return;
    }

    try {
      const response = await sendMutation.mutateAsync({ actionId });

      if (response?.success) {
        // Complete the action in the background, but keep this modal mounted for the
        // success step - refetching the list would unmount the card and close the popup.
        await onSuccess?.();
        setStep('success');
      } else {
        showToast(TRAUMA_CONTACT_ERROR_MESSAGE, undefined, 'error');
      }
    } catch {
      showToast(TRAUMA_CONTACT_ERROR_MESSAGE, undefined, 'error');
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Modal
      open={open}
      setOpen={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
      overlay
      onTapBackgroundClose={!isSubmitting && step === 'confirm'}
      className={styles.modal}
      aria-labelledby={
        step === 'confirm' ? 'trauma-contact-modal-title' : 'trauma-contact-success-title'
      }
    >
      {step === 'confirm' ? (
        <div className={styles.content}>
          <div className={styles.headerIcon} aria-hidden="true">
            <span className={styles.handshakeEmoji} role="img">
              🤝
            </span>
          </div>

          <h2 id="trauma-contact-modal-title" className={styles.title}>
            {TRAUMA_CONTACT_MODAL_TITLE}
          </h2>
          <p className={styles.intro}>{TRAUMA_CONTACT_MODAL_INTRO}</p>

          <div className={styles.identityCard}>
            <div className={styles.identityCardHeader}>
              <WarningAmberRoundedIcon className={styles.warningIcon} aria-hidden="true" />
              <h3 className={styles.identityHeading}>{TRAUMA_CONTACT_IDENTITY_HEADING}</h3>
            </div>
            <p className={styles.identityBody}>{TRAUMA_CONTACT_IDENTITY_BODY}</p>
            <div className={styles.identityDetails}>
              <p>
                <span className={styles.detailLabel}>{TRAUMA_CONTACT_FULL_NAME_LABEL}</span>{' '}
                {identity.fullName}
              </p>
              <p>
                <span className={styles.detailLabel}>{TRAUMA_CONTACT_EMAIL_LABEL}</span>{' '}
                {identity.email}
              </p>
            </div>
          </div>

          <div className={styles.consentRow}>
            <Checkbox
              id={`trauma-contact-consent-${actionId}`}
              name={`trauma-contact-consent-${actionId}`}
              checked={consented}
              onChange={(event) => setConsented(event.target.checked)}
              disabled={isSubmitting}
              className={styles.consentCheckbox}
            />
            <label className={styles.consentLabel} htmlFor={`trauma-contact-consent-${actionId}`}>
              {TRAUMA_CONTACT_CONSENT_LABEL}
            </label>
          </div>

          <div className={styles.btnContainer}>
            <SystemButton
              type="button"
              size="medium"
              variant="outlinedPrimarySkyBlue"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {TRAUMA_CONTACT_CANCEL_LABEL}
            </SystemButton>
            <SystemButton
              type="button"
              size="medium"
              variant="primarySkyBlue"
              onClick={handleConfirm}
              disabled={!consented || isSubmitting}
            >
              {TRAUMA_CONTACT_CONFIRM_LABEL} →
            </SystemButton>
          </div>
        </div>
      ) : (
        <div className={styles.successContent}>
          <div className={styles.successIcon} aria-hidden="true">
            <CheckIcon className={styles.checkIcon} />
          </div>
          <h2 id="trauma-contact-success-title" className={styles.successTitle}>
            {TRAUMA_CONTACT_SUCCESS_TITLE}
          </h2>
          <p className={styles.successDescription}>
            {TRAUMA_CONTACT_SUCCESS_DESCRIPTION_PREFIX}{' '}
            <a className={styles.emailLink} href={`mailto:${identity.email}`}>
              {identity.email}
            </a>{' '}
            {TRAUMA_CONTACT_SUCCESS_DESCRIPTION_SUFFIX}
          </p>
          <SystemButton
            type="button"
            size="medium"
            variant="primarySkyBlue"
            onClick={() => {
              onDone?.();
            }}
            fullWidthButton
          >
            {TRAUMA_CONTACT_DONE_LABEL}
          </SystemButton>
        </div>
      )}
    </Modal>
  );
};

TraumaContactDrawer.defaultProps = {
  onSuccess: undefined,
  onDone: undefined,
};
